import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  listAllTaskTags,
  listAllTasks,
  type Task,
  type TaskSort,
  type TaskSummary,
} from '../lib/tasks';
import { orderTasks } from '../lib/taskGroups';
import {
  isTaskGrouping,
  readStoredGrouping,
  storeGrouping,
  type TaskGrouping,
} from '../lib/taskGrouping';
import {
  useTaskListState,
  type CreateInBackground,
} from '../components/tasks/useTaskListState';
import { listProjects, type Project } from '../lib/projects';
import { isTaskStatus, UNFINISHED_STATUSES, type TaskStatus } from '../lib/taskStatus';
import { listAssignees, type Assignee } from '../lib/assignees';
import { useAuth } from '../lib/auth';
import {
  Alert,
  Button,
  CountBadge,
  EmptyState,
  Input,
  FOCUS_RING_FLUSH,
  PageHeader,
  SEGMENTED_TRACK,
  Select,
  SkeletonList,
  segmentedItem,
} from '../components/ui';
import { useHideCompleted } from '../components/tasks/useHideCompleted';
import { TaskList } from '../components/tasks/TaskList';
import { BulkActionBar } from '../components/tasks/BulkActionBar';
import { SidePanel } from '../components/tasks/SidePanel';
import { TaskDetail } from '../components/tasks/TaskDetail';
import { NewTaskForm } from '../components/tasks/NewTaskForm';
import { TASK_GRID_WITH_PROJECT } from '../components/tasks/taskGrid';
import { CloseIcon, InboxIcon, PlusIcon, RefreshIcon, SearchIcon } from '../components/icons';

/** Reserved id for the unsaved-task panel, exactly as the project tab uses it:
 *  real ids are uuids, so this can never collide with one. */
const NEW_TASK = 'new';

/**
 * How many rows the page asks for.
 *
 * The endpoint is the only paginated one in the API, and it has to be: a
 * workspace-wide backlog grows without bound. This is deliberately far above a
 * screenful so the common case is the whole list, with `total` telling us when
 * it is not -- and Load more raises it by the same step rather than paging,
 * because every optimistic edit on this page is keyed to the array in hand.
 */
const PAGE_SIZE = 200;

/** What the composer hands the panel when it is opened from the toolbar. */
type ComposeHandoff = { title: string; status: TaskStatus };

/**
 * The secondary order, worded as it is on the project tab -- due date decides
 * the list (see lib/taskGroups.ts) and this picks the tail.
 */
const SORT_LABELS: Record<TaskSort, string> = {
  due: 'Then by oldest',
  priority: 'Then by priority',
  created: 'Then by newest',
  updated: 'Then by recently updated',
  title: 'Then by title',
};

const isSort = (value: string | null): value is TaskSort => value !== null && value in SORT_LABELS;

const GROUPINGS: { value: TaskGrouping; label: string }[] = [
  { value: 'status', label: 'Group by status' },
  { value: 'none', label: 'No grouping' },
];

type AllTasksContext = {
  tasks: TaskSummary[];
  projects: Project[];
  assignees: Assignee[];
  tagSuggestions: string[];
  upsertTask: (task: TaskSummary) => void;
  removeTask: (taskId: string) => void;
  createInBackground: CreateInBackground;
  hrefFor: (taskId: string) => string;
};

const useAllTasksContext = () => useOutletContext<AllTasksContext>();

/**
 * Every task in the workspace, whatever project it lives in.
 *
 * Structurally the project tab with the project taken out of the URL and put
 * into a column: the same rows, the same inline edits, the same bulk bar and
 * the same panel, all through the shared `useTaskListState`. What it adds is a
 * project filter, a project column, and a grouping toggle -- and what it drops
 * is the inline add row, which has no project to file into.
 *
 * Like the project tab this is the route's own element rather than an index
 * route: an index route unmounts the moment `:taskId` matches, and the panel is
 * an overlay *over* the list, which has to stay mounted underneath.
 */
export function AllTasks() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [projects, setProjects] = useState<Project[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const { user } = useAuth();

  const projectId = searchParams.get('project') ?? '';
  const assigneeId = searchParams.get('assignee') ?? '';
  const tag = searchParams.get('tag') ?? '';
  const q = searchParams.get('q') ?? '';
  const sortParam = searchParams.get('sort');
  const sort: TaskSort = isSort(sortParam) ? sortParam : 'due';

  /**
   * Finished work is out of the way unless it is asked for.
   *
   * Dropped at the fetch rather than after it, because this is the one
   * paginated list in the app: filtering a page of 200 down on the client would
   * spend the page's budget on rows nobody wanted to see, and a workspace with a
   * long tail of completed tasks would need Load more to reach its open ones.
   *
   * In the URL rather than in storage -- the same place the Assignees page keeps
   * its archived toggle, and for the same reason: "everything, finished
   * included" is a view worth sending someone. Not part of `filtered` below,
   * though; hiding these is the page's resting state, not something anyone
   * turned on, and a chip offering to clear a filter nobody set would be a lie.
   */
  const showDone = searchParams.get('done') === '1';

  /**
   * The grouping preference, URL first and storage second.
   *
   * The same arrangement Projects.tsx uses for its card/list view: a `?group=`
   * makes a link carry the view it was sent with, and without one the choice
   * falls back to whatever this person last picked. The default is deliberately
   * never written to the URL -- doing so would pin it and quietly override the
   * stored preference on every later visit.
   */
  const groupParam = searchParams.get('group');
  const [storedGrouping, setStoredGrouping] = useState<TaskGrouping>(readStoredGrouping);
  const grouping: TaskGrouping = isTaskGrouping(groupParam) ? groupParam : storedGrouping;

  const setGrouping = useCallback(
    (next: TaskGrouping) => {
      setStoredGrouping(next);
      storeGrouping(next);
      updateParams({ group: next });
    },
    // `updateParams` closes over `searchParams`, and re-reading it is exactly
    // what keeps this writing onto the current filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams],
  );

  const {
    tasks,
    total,
    loading,
    refreshing,
    refresh,
    loadError,
    actionError,
    setActionError,
    failedCreates,
    dismissCreate,
    retryCreate,
    selected,
    toggleSelected,
    clearSelection,
    upsertTask,
    removeTask,
    createInBackground,
    patchTask,
    patchSelected,
    flashId,
  } = useTaskListState({
    load: (signal) =>
      listAllTasks(
        {
          ...(projectId ? { projectId } : {}),
          ...(assigneeId ? { assigneeId } : {}),
          ...(tag ? { tag } : {}),
          ...(q ? { q } : {}),
          ...(showDone ? {} : { status: UNFINISHED_STATUSES }),
          sort,
          limit,
        },
        signal,
      ),
    loadDeps: [projectId, assigneeId, tag, q, showDone, sort, limit],
    // Not keyed on `sort`, `limit` or the grouping: all three re-order or
    // extend the same list rather than making it a different one. `showDone`
    // is, because it decides which rows exist.
    resetSelectionOn: [projectId, assigneeId, tag, q, showDone],
    // Nothing to default to -- every create here comes from the composer, which
    // asks. See the guard in `useTaskListState`.
    defaultProjectId: null,
    projectFor: (id) => projects.find((project) => project.id === id) ?? null,
    orderFor: (entries) => orderTasks(entries, grouping),
    assignees,
    user,
  });

  // Loaded once and not re-filtered: they populate controls, and an empty list
  // degrades to a usable one. Failures are silent for the same reason.
  useEffect(() => {
    const controller = new AbortController();

    void Promise.all([
      listProjects({}, controller.signal).then((data) => setProjects(data.projects)),
      // Archived people included on purpose: the picker needs whoever is
      // already on a task even after they leave, and filtering by someone who
      // has gone is exactly the question you ask after they go.
      listAssignees({ includeArchived: true }, controller.signal).then((data) =>
        setAssignees(data.assignees),
      ),
      listAllTaskTags(controller.signal).then((data) => setTagSuggestions(data.tags)),
    ]).catch(() => {});

    return () => controller.abort();
  }, []);

  // A narrower question deserves a fresh page rather than the deep one the
  // last query was scrolled out to.
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [projectId, assigneeId, tag, q, showDone]);

  /**
   * The search box types locally and reaches the URL on a delay -- the same
   * buffer the project tab keeps, and for the same reason: the URL is what the
   * fetch keys on, so writing every keystroke to it was one aborted request per
   * character and a list flickering through its loading state as you typed.
   */
  const [searchDraft, setSearchDraft] = useState(q);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  useEffect(() => {
    if (searchDraft === q) return;
    const timer = window.setTimeout(() => updateParams({ q: searchDraft }), 200);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft, q, searchParams]);

  function updateParams(changes: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  // Every task link carries the current filters: without them, opening a task
  // would silently reset the list sitting behind the panel.
  const hrefFor = useCallback(
    (taskId: string) => `/tasks/${taskId}${location.search}`,
    [location.search],
  );

  const filtered = projectId !== '' || assigneeId !== '' || tag !== '' || q !== '';

  const openTaskId = useMatch('/tasks/:taskId')?.params.taskId ?? null;
  const activeTaskId = openTaskId === NEW_TASK ? null : openTaskId;

  /**
   * Escape drops the selection -- unless a popover has already handled it, or
   * a panel is open, in which case the key belongs to the panel and firing
   * both listeners would close two things at once.
   */
  useEffect(() => {
    if (selected.size === 0 || openTaskId !== null) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      clearSelection();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selected.size, openTaskId, clearSelection]);

  /** The filters ride along in the URL; the half-typed title rides as router
   *  state, because `location.search` belongs to the filters and a `?title=`
   *  would tangle with them. */
  const openCompose = useCallback(
    (title: string, status: TaskStatus) => {
      navigate(
        { pathname: `/tasks/${NEW_TASK}`, search: location.search },
        { state: { title, status } satisfies ComposeHandoff },
      );
    },
    [navigate, location.search],
  );

  /* Finished rows leave a few seconds after they finish, while completed work
     is hidden. Shared with the project tab. */
  useHideCompleted({ tasks, showDone, activeTaskId, removeTask });

  const truncated = total !== null && total > tasks.length;

  return (
    <div>
      <PageHeader
        title="Tasks"
        description="Everything across every project."
        actions={
          <Button variant="primary" onClick={() => openCompose('', 'TODO')}>
            <PlusIcon />
            New task
          </Button>
        }
      />

      {/* One toolbar row, as on the project tab: the search box takes the
          slack and every picker keeps a fixed width so the row does not reflow
          as selections change. Each control is wrapped rather than given
          `w-auto` -- the primitives carry `w-full`, which Tailwind emits after
          `w-auto` and so wins whatever order the classes are written in. */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="grid min-w-[10rem] flex-1 items-center [grid-template-areas:'field'] sm:max-w-xs">
          <Input
            value={searchDraft}
            placeholder="Search tasks"
            aria-label="Search tasks"
            className="pl-9 [grid-area:field]"
            onChange={(event) => setSearchDraft(event.target.value)}
          />
          <SearchIcon className="pointer-events-none ml-3 h-4 w-4 justify-self-start text-stone-400 [grid-area:field]" />
        </div>

        <div className="w-40">
          <Select
            value={projectId}
            aria-label="Filter by project"
            onChange={(event) => updateParams({ project: event.target.value })}
          >
            <option value="">All projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
            {/* A project that has since gone would otherwise leave the box
                blank over an empty list, with nothing saying a filter is on. */}
            {projectId && !projects.some((project) => project.id === projectId) ? (
              <option value={projectId}>Unknown project</option>
            ) : null}
          </Select>
        </div>

        <div className="w-36">
          <Select
            value={assigneeId}
            aria-label="Filter by assignee"
            onChange={(event) => updateParams({ assignee: event.target.value })}
          >
            {/* "" means Anyone here and Unassigned in the task panel's picker,
                which is why this one spells Unassigned out rather than reusing
                the blank value. */}
            <option value="">Anyone</option>
            <option value="none">Unassigned</option>
            {assignees
              .filter((assignee) => !assignee.archived)
              .map((assignee) => (
                <option key={assignee.id} value={assignee.id}>
                  {assignee.name}
                </option>
              ))}
            {assignees.some((assignee) => assignee.archived) ? (
              <optgroup label="Archived">
                {assignees
                  .filter((assignee) => assignee.archived)
                  .map((assignee) => (
                    <option key={assignee.id} value={assignee.id}>
                      {assignee.name}
                    </option>
                  ))}
              </optgroup>
            ) : null}
            {assigneeId && assigneeId !== 'none' && !assignees.some((a) => a.id === assigneeId) ? (
              <option value={assigneeId}>Unknown assignee</option>
            ) : null}
          </Select>
        </div>

        <div className="w-36">
          <Select
            value={tag}
            aria-label="Filter by tag"
            onChange={(event) => updateParams({ tag: event.target.value })}
          >
            <option value="">Any tag</option>
            {tagSuggestions.map((suggestion) => (
              <option key={suggestion} value={suggestion}>
                {suggestion}
              </option>
            ))}
          </Select>
        </div>

        {/* The house toggle, worded and shaped exactly as the Assignees page's
            archived one: a button whose label says what the next click does,
            with `aria-pressed` carrying the state a sighted reader gets from
            the word itself. Not a segmented pair -- "show" and "hide" are one
            thing on or off, not two views to choose between. */}
        <Button
          aria-pressed={showDone}
          onClick={() => updateParams({ done: showDone ? '' : '1' })}
        >
          {showDone ? 'Hide completed' : 'Show completed'}
        </Button>

        <div className="w-48">
          <Select
            value={sort}
            aria-label="Sort tasks"
            onChange={(event) => updateParams({ sort: event.target.value })}
          >
            {(Object.keys(SORT_LABELS) as TaskSort[]).map((option) => (
              <option key={option} value={option}>
                {SORT_LABELS[option]}
              </option>
            ))}
          </Select>
        </div>

        {/* The house segmented control, the same one the dashboard's due window
            and the projects card/list switch use. Two labelled buttons rather
            than a switch: "grouped" and "flat" are two views, and a bare toggle
            would not say which one it is currently showing. */}
        <div role="group" aria-label="Grouping" className={SEGMENTED_TRACK}>
          {GROUPINGS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={grouping === value}
              onClick={() => setGrouping(value)}
              className={segmentedItem(grouping === value)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Last in the row, and after the controls that change *what* the list
            is: this one changes nothing about the query, it only goes and asks
            again. Every edit on this page is optimistic and nothing polls, so a
            tab left open is the one place the list can be wrong -- and reloading
            the browser to fix it throws away the filters. The icon drops out
            while the request is in the air and the Spinner takes its place, so
            the button keeps its width. */}
        <Button loading={refreshing} disabled={loading} onClick={refresh}>
          {refreshing ? null : <RefreshIcon />}
          Refresh
        </Button>
      </div>

      {filtered ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-stone-500">
          <span>Filtered by</span>
          {q ? <FilterChip onClear={() => updateParams({ q: '' })}>“{q}”</FilterChip> : null}
          {projectId ? (
            <FilterChip onClear={() => updateParams({ project: '' })}>
              {projects.find((entry) => entry.id === projectId)?.name ?? 'Unknown project'}
            </FilterChip>
          ) : null}
          {assigneeId ? (
            <FilterChip onClear={() => updateParams({ assignee: '' })}>
              {assigneeId === 'none'
                ? 'Unassigned'
                : (assignees.find((entry) => entry.id === assigneeId)?.name ?? 'Unknown assignee')}
            </FilterChip>
          ) : null}
          {tag ? <FilterChip onClear={() => updateParams({ tag: '' })}>#{tag}</FilterChip> : null}
          <Button
            compact
            variant="ghost"
            onClick={() => updateParams({ q: '', project: '', assignee: '', tag: '' })}
          >
            Clear all
          </Button>

          {loading ? null : (
            <span className="ml-auto">
              <CountBadge>
                {tasks.length === 1 ? '1 match' : `${tasks.length} matches`}
              </CountBadge>
            </span>
          )}
        </div>
      ) : null}

      {failedCreates.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {failedCreates.map((failure) => (
            <li key={failure.id}>
              <Alert onDismiss={() => dismissCreate(failure.id)}>
                <span className="flex flex-wrap items-center gap-3">
                  <span className="min-w-0 max-w-sm truncate">
                    “{failure.input.title}” was not saved.
                  </span>
                  <Button compact onClick={() => retryCreate(failure)}>
                    Try again
                  </Button>
                </span>
              </Alert>
            </li>
          ))}
        </ul>
      ) : null}

      {actionError ? (
        <div className="mt-4">
          {/* Above the list rather than in place of it: the rows this is about
              are the ones you are looking at. */}
          <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
        </div>
      ) : null}

      {selected.size > 0 ? (
        <BulkActionBar
          count={selected.size}
          onPatch={(patch) => void patchSelected(patch)}
          onClear={clearSelection}
        />
      ) : null}

      <div className="mt-6">
        {loadError ? (
          <Alert>{loadError}</Alert>
        ) : loading ? (
          <SkeletonList rows={8} />
        ) : tasks.length === 0 ? (
          <EmptyState
            icon={filtered ? <SearchIcon className="h-5 w-5" /> : <InboxIcon className="h-5 w-5" />}
            title={filtered ? 'No matching tasks' : 'No tasks yet'}
            action={
              <div className="flex flex-wrap justify-center gap-2">
                {filtered ? (
                  <Button
                    onClick={() => updateParams({ q: '', project: '', assignee: '', tag: '' })}
                  >
                    Clear filters
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => openCompose('', 'TODO')}>
                    <PlusIcon />
                    New task
                  </Button>
                )}
                {/* An empty list with completed work hidden most often means the
                    work is finished rather than absent, so the way to see it is
                    offered here instead of leaving someone to find the toolbar
                    toggle that emptied the page. */}
                {showDone ? null : (
                  <Button onClick={() => updateParams({ done: '1' })}>Show completed</Button>
                )}
              </div>
            }
          >
            {filtered
              ? 'Nothing here matches all of them.'
              : 'Tasks from every project will show up here.'}
            {showDone ? null : ' Completed tasks are hidden.'}
          </EmptyState>
        ) : (
          <>
            <TaskList
              tasks={tasks}
              activeTaskId={activeTaskId}
              selectedIds={selected}
              hrefFor={hrefFor}
              flashId={flashId}
              grouping={grouping}
              grid={TASK_GRID_WITH_PROJECT}
              showProject
              onToggleSelected={toggleSelected}
              onPatch={(task, patch) => void patchTask(task, patch)}
              // No `onAdd`: an inline add row files into the status it sits
              // under, and there is no project here for it to file into. The
              // New task button asks for one instead.
            />

            {truncated ? (
              // Says what is missing rather than only that something is. A
              // silent cap on a page whose whole job is "everything" is the one
              // thing this list must not do.
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm text-stone-500">
                <span>
                  Showing {tasks.length} of {total}.
                </span>
                <Button compact onClick={() => setLimit((current) => current + PAGE_SIZE)}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* The panel renders here but is `fixed`, so it escapes this column. */}
      <Outlet
        context={
          {
            tasks,
            projects,
            assignees,
            tagSuggestions,
            upsertTask,
            removeTask,
            createInBackground,
            hrefFor,
          } satisfies AllTasksContext
        }
      />
    </div>
  );
}

/**
 * One active filter, with its own off switch. A sibling of the project tab's
 * chip -- deliberately not a `Badge`, because a badge is a label and every one
 * of these is a control.
 */
function FilterChip({ children, onClear }: { children: ReactNode; onClear: () => void }) {
  return (
    <span className="inline-flex max-w-[12rem] items-center gap-1 rounded-full bg-stone-100 py-0.5 pl-2.5 pr-1 font-medium text-stone-700">
      <span className="truncate">{children}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label="Remove this filter"
        className={`rounded-full p-0.5 text-stone-500 transition hover:bg-stone-200 hover:text-stone-900 ${FOCUS_RING_FLUSH}`}
      >
        <CloseIcon />
      </button>
    </span>
  );
}

/**
 * The panel route, a sibling of the project tab's rather than a shared one:
 * the two differ in where Close goes, and in that this one has to narrow the
 * task list it hands down to a single project.
 */
export function AllTasksPanelRoute() {
  const { taskId } = useParams<{ taskId: string }>();
  const { tasks, projects, assignees, tagSuggestions, upsertTask, removeTask, createInBackground, hrefFor } =
    useAllTasksContext();
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();

  const open = tasks.find((task) => task.id === taskId);

  /**
   * The candidates for the parent and prerequisite pickers, narrowed to the
   * open task's own project.
   *
   * `tasks` here spans the workspace, and TaskDetail feeds this one array to
   * three things that must not: the parent combobox, `descendantIds`, and the
   * prerequisite options. The server rejects a cross-project parent outright
   * ("A parent task must be in the same project"), so an unfiltered list would
   * be offering choices it knows will fail.
   */
  const projectTasks = useMemo(
    () => (open ? tasks.filter((task) => task.projectId === open.projectId) : []),
    [tasks, open],
  );

  if (!taskId) return null;

  const close = () => navigate({ pathname: '/tasks', search: location.search });

  // Router state is only ever absent (a cold reload of /tasks/new drops it), so
  // both halves fall back rather than trusting the shape.
  const handoff = (location.state ?? {}) as Partial<ComposeHandoff>;

  return (
    <SidePanel labelledBy={titleId} focusKey={taskId} onClose={close}>
      {taskId === NEW_TASK ? (
        <NewTaskForm
          key={NEW_TASK}
          titleId={titleId}
          initialTitle={typeof handoff.title === 'string' ? handoff.title : ''}
          initialStatus={isTaskStatus(handoff.status ?? null) ? handoff.status! : 'TODO'}
          // The whole workspace; the form narrows it to whichever project is
          // picked, because a parent from another one would be rejected.
          tasks={tasks}
          assignees={assignees}
          tagSuggestions={tagSuggestions}
          projects={projects}
          onCreate={(input, projectId) => createInBackground({ ...input, projectId })}
        />
      ) : (
        <TaskDetail
          // Remounted per task: params change without remounting the route
          // element, so an open title editor or a half-typed comment would
          // otherwise carry over to the next task.
          key={taskId}
          taskId={taskId}
          seed={open}
          tasks={projectTasks}
          assignees={assignees}
          tagSuggestions={tagSuggestions}
          titleId={titleId}
          hrefFor={hrefFor}
          createInBackground={createInBackground}
          onTaskChanged={(task: Task) => upsertTask(task)}
          onTaskDeleted={removeTask}
        />
      )}
    </SidePanel>
  );
}
